import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PopupService } from '../../../services/popup.service';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth.service';
import { BillingService } from '../../../services/billing.service';
import { filter, Observable, Subscription, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../state/app.state';
import {
  googleCallback,
  googleLogin,
  googleLogout,
  hubspotCallback,
  hubspotLogin,
  hubspotLogout,
} from '../../../state/oauth/oauth.actions';
import {
  selectGoogleCallbackFailure,
  selectGoogleCallbackSuccess,
  selectGoogleLoginFailure,
  selectGoogleLoginSuccess,
  selectGoogleLogoutFailure,
  selectGoogleLogoutSuccess,
  selectHubspotCallbackFailure,
  selectHubspotCallbackSuccess,
  selectHubspotLoginFailure,
  selectHubspotLoginSuccess,
  selectHubspotLogoutFailure,
  selectHubspotLogoutSuccess,
} from '../../../state/oauth/oauth.selectors';
import * as LivespaceActions from '../../../state/livespace/livespace.actions';
import {
  selectLivespaceConnected,
  selectLivespaceConnecting,
  selectLivespaceConnectError,
} from '../../../state/livespace/livespace.selectors';
import { updateUser } from '../../../state/auth/auth.actions';
import { CommonModule } from '@angular/common';
import { LivespaceConnectDialogComponent } from '../../../components/livespace-connect-dialog/livespace-connect-dialog.component';
import { AccountCancellationComponent } from './account-cancellation/account-cancellation.component';
import { AccountTabsComponent } from '../account-tabs/account-tabs.component';
import { selectCredits } from '../../../state/subscription/subscription.selectors';
import { MatTooltip } from '@angular/material/tooltip';
import { HeaderService } from '../../../services/header.service';
import { AppTourService } from '../../../services/app-tour.service';

@Component({
  selector: 'app-new-account',
  standalone: true,
  imports: [
    CommonModule,
    AccountCancellationComponent,
    RouterLink,
    AccountTabsComponent,
    MatTooltip,
    LivespaceConnectDialogComponent,
  ],
  templateUrl: 'account.component.html',
  styleUrls: ['account.component.scss'],
})
export class AccountComponent implements OnInit {
  isLoading = false;
  user: any;
  billing_url: string = '';
  isConnectedToHubSpot = false;
  isConnectedToGoogle = false;
  isConnectedToLivespace = false;
  showLivespaceConnectDialog = false;
  code: string;
  type: string;

  showDeactivateAccountModal = false;
  credits$: Observable<number | null>;

  // Initialize state object to prevent template errors
  state: { isLoading: boolean } = { isLoading: false };

  window = window;

  constructor(
    private store: Store<AppState>,
    private authService: AuthService,
    private router: Router,
    private popupService: PopupService,
    private billingService: BillingService,
    private activatedRoute: ActivatedRoute,
    private headerService: HeaderService,
    private appTourService: AppTourService,
  ) {
    this.authService.authUser().subscribe((user) => {
      this.user = user;
    });

    this.billing_url = environment.billing_url;
  }
  ngOnInit(): void {

    this.headerService.setHeaderState('Account Settings', null, true);

    this.authService.authUser().subscribe((user) => {
      this.user = user;
      if (this.user) {
        this.isConnectedToHubSpot = this.user.has_connected_to_hubspot;
        this.isConnectedToGoogle = this.user.has_connected_to_google;
        this.isConnectedToLivespace = this.user.has_connected_to_livespace;
      }
    });

    this.authService.authState().subscribe((state) => {
      this.state = state;
    });

    this.activatedRoute.queryParams.subscribe((queryParams) => {
      this.code = queryParams['code'];
      this.type = queryParams['type'];
    });

    // Check if the query parameters contain a parameter called "code"
    if (this.code && this.type == 'google') {
      this.callbackGoogle();
    } else if (this.code) {
      this.isLoading = true;
      this.store.dispatch(
        hubspotCallback({ code: this.code, user_id: this.user.id })
      );

      const hubspotCallbackSuccess$ = this.store.select(
        selectHubspotCallbackSuccess
      );
      const hubspotCallbackFailure$ = this.store.select(
        selectHubspotCallbackFailure
      );

      let hubspotCallbackSuccessSubscription: Subscription = null;
      let hubspotCallbackFailureSubscription: Subscription = null;

      hubspotCallbackSuccessSubscription = hubspotCallbackSuccess$
        .pipe(
          // Trying with filter data.error === null but not working
          filter((data) => data.isConnectedToHubspot == true),
          take(1)
        )
        .subscribe({
          next: (data) => {
            // Handle success
            this.popupService.notify(
              'You have successfully login into hubspot!',
              'Login success',
              'success'
            );

            this.isLoading = false;

            // this.update_state_user();
            const updatedUser = { ...this.user, has_connected_to_hubspot: 1 };
            this.store.dispatch(updateUser({ user: updatedUser }));

            this.router.navigate(['settings/account']);

            if (hubspotCallbackSuccessSubscription) {
              hubspotCallbackSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
            }
          },
          error: (error) => {
            // Handle error
            console.error('Hubspot Callback error:', error);

            if (hubspotCallbackSuccessSubscription) {
              hubspotCallbackSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
            }
          },
        });

      hubspotCallbackFailureSubscription = hubspotCallbackFailure$
        .pipe(
          filter((failure) => failure !== null), // Filter for the failure condition, assuming failure is null when there's no error
          take(1) // Take the first occurrence
        )
        .subscribe({
          next: (error) => {
            // Handle failure

            this.popupService.notify(error, 'Hubspot Callback error', 'error');

            if (hubspotCallbackFailureSubscription) {
              hubspotCallbackFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
            }
          },
          error: (error) => {
            // Handle error in error handling
            console.error('Hubspot Callback error handling error:', error);

            if (hubspotCallbackFailureSubscription) {
              hubspotCallbackFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
            }
          },
        });
    }

    this.authService.isLoggedIn().subscribe((isLoggedIn) => {
      this.credits$ = this.store.select(selectCredits);
    });
  }

  notifiyFeatureUpcoming() {
    this.popupService.notify(
      "Thank you for your interest in this feature! While this feature is not ready yet, we've added you to our waiting list.  We're thrilled to have you on board and look forward to bringing this feature to you",
      'Feature coming soon!',
      'error'
    );
  }

  authorizeHubspot() {
    this.store.dispatch(hubspotLogin());

    const hubspotLoginSuccess$ = this.store.select(selectHubspotLoginSuccess);
    const hubspotLoginFailure$ = this.store.select(selectHubspotLoginFailure);

    let hubspotLoginSuccessSubscription: Subscription = null;
    let hubspotLoginFailureSubscription: Subscription = null;

    hubspotLoginSuccessSubscription = hubspotLoginSuccess$
      .pipe(
        // filter(data => data.url != null),
        take(1)
      )
      .subscribe({
        next: () => {
          // Handle success

          if (hubspotLoginSuccessSubscription) {
            hubspotLoginSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
        error: (error) => {
          // Handle error
          console.error('Hubspot Login error:', error);

          if (hubspotLoginSuccessSubscription) {
            hubspotLoginSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
      });

    hubspotLoginFailureSubscription = hubspotLoginFailure$
      .pipe(
        filter((failure) => failure !== null), // Filter for the failure condition, assuming failure is null when there's no error
        take(1) // Take the first occurrence
      )
      .subscribe({
        next: (error) => {
          // Handle failure
          const errorMessage = error.error.error.message;
          this.popupService.notify(
            errorMessage,
            'Hubspot Login error',
            'error'
          );

          if (hubspotLoginFailureSubscription) {
            hubspotLoginFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
        error: (error) => {
          // Handle error in error handling
          console.error('Login error handling error:', error);

          if (hubspotLoginFailureSubscription) {
            hubspotLoginFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
      });
  }

  authorizeGoogle() {
    this.store.dispatch(googleLogin());

    // Listen for success response
    this.store
      .select(selectGoogleLoginSuccess)
      .pipe(
        filter((result) => !!result),
        take(1) // Automatically unsubscribe after one emission
      )
      .subscribe({
        error: (error) => {
          console.error('Error loggin in to google info:', error);
        },
      });

    // Handle failure response
    this.store
      .select(selectGoogleLoginFailure)
      .pipe(
        filter((error) => !!error),
        take(1) // Automatically unsubscribe after one emission
      )
      .subscribe({
        next: (error) => {
          if (error) {
            console.error('Failed loggin in to google  info:', error);
            // Optional: Show a notification or take other appropriate actions
          }
        },
      });
  }

  callbackGoogle() {
    this.store.dispatch(
      googleCallback({ code: this.code, user_id: this.user.id })
    );

    // Listen for success response
    this.store
      .select(selectGoogleCallbackSuccess)
      .pipe(
        filter((result) => !!result),
        take(1) // Automatically unsubscribe after one emission
      )
      .subscribe({
        next: (data) => {
          // Handle success
          this.popupService.notify(
            'You have successfully login into google!',
            'Login success',
            'success'
          );

          this.isLoading = false;

          // this.update_state_user();
          const updatedUser = { ...this.user, has_connected_to_google: 1 };
          this.store.dispatch(updateUser({ user: updatedUser }));

          this.router.navigate(['settings/account']);
        },
        error: (error) => {
          console.error('Error loggin in to google info:', error);
        },
      });

    // Handle failure response
    this.store
      .select(selectGoogleCallbackFailure)
      .pipe(
        filter((error) => !!error),
        take(1) // Automatically unsubscribe after one emission
      )
      .subscribe({
        next: (error) => {
          if (error) {
            console.error('Failed google callback info:', error);
            // Optional: Show a notification or take other appropriate actions
          }
        },
      });
  }

  disconnectHubspot() {
    this.store.dispatch(hubspotLogout({ user_id: this.user.id }));

    const hubspotLogoutSuccess$ = this.store.select(selectHubspotLogoutSuccess);
    const hubspotLogoutFailure$ = this.store.select(selectHubspotLogoutFailure);

    let hubspotLogoutSuccessSubscription: Subscription = null;
    let hubspotLogoutFailureSubscription: Subscription = null;

    hubspotLogoutSuccessSubscription = hubspotLogoutSuccess$
      .pipe(
        filter((data) => data.isConnectedToHubspot === false),
        take(1)
      )
      .subscribe({
        next: (data) => {
          // this.update_state_user();

          const updatedUser = { ...this.user, has_connected_to_hubspot: 0 };
          this.store.dispatch(updateUser({ user: updatedUser }));

          this.popupService.notify(
            "You've successfully disconnected from HubSpot",
            'Hubspot Logout ',
            'success'
          );
          // Handle success
          if (hubspotLogoutSuccessSubscription) {
            hubspotLogoutSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
        error: (error) => {
          // Handle error
          console.error('Hubspot Login error:', error);

          if (hubspotLogoutSuccessSubscription) {
            hubspotLogoutSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
      });

    hubspotLogoutFailureSubscription = hubspotLogoutFailure$
      .pipe(
        filter((failure) => failure !== null), // Filter for the failure condition, assuming failure is null when there's no error
        take(1) // Take the first occurrence
      )
      .subscribe({
        next: (error) => {
          // Handle failure
          const errorMessage = error.error.error.message;
          this.popupService.notify(
            errorMessage,
            'Hubspot Logout error',
            'error'
          );

          if (hubspotLogoutFailureSubscription) {
            hubspotLogoutFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
        error: (error) => {
          // Handle error in error handling
          console.error('Logout error handling error:', error);

          if (hubspotLogoutFailureSubscription) {
            hubspotLogoutFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
      });
  }

  disconnectGoogle() {
    this.store.dispatch(googleLogout({ user_id: this.user.id }));

    // Listen for success response
    this.store
      .select(selectGoogleLogoutSuccess)
      .pipe(
        filter((data) => data.isConnectedToGoogle === false),
        take(1)
      )
      .subscribe({
        next: () => {
          // this.update_state_user();

          const updatedUser = { ...this.user, has_connected_to_google: 0 };
          this.store.dispatch(updateUser({ user: updatedUser }));

          this.popupService.notify(
            "You've successfully disconnected from Google",
            'Google Logout ',
            'success'
          );
        },
        error: (error) => {
          console.error('Error loggin in to google info:', error);
        },
      });

    // Handle failure response
    this.store
      .select(selectGoogleLogoutFailure)
      .pipe(
        filter((error) => !!error),
        take(1) // Automatically unsubscribe after one emission
      )
      .subscribe({
        next: (error) => {
          if (error) {
            console.error('Failed logout in to google  info:', error);
            // Optional: Show a notification or take other appropriate actions
          }
        },
      });
  }

  deactivateAccount() {
    this.showDeactivateAccountModal = true;
  }

  handleDeactivateAccountClose() {
    this.showDeactivateAccountModal = false;
  }

  openBillingPortal() {
    this.billingService.openBillingPortal();
  }

  onImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = 'assets/images/human.svg'; // Fallback image when error occurs
  }

  copyProfileUrl() {
    const profileUrl = `${window.location.origin}/${this.user.public_profile_url}`;

    // Check if the Clipboard API is supported
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(profileUrl)
        .then(() => {
          // Provide feedback
          this.popupService.notify('Profile URL copied to clipboard!', 'Profile URL', 'success', 2500);
        })
        .catch((err) => {
          console.error('Failed to copy text to clipboard:', err);
        });
    } else {
      // Fallback for unsupported browsers
      this.fallbackCopyTextToClipboard(profileUrl);
    }
  }

  // Fallback method for unsupported browsers
  private fallbackCopyTextToClipboard(text: string) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed'; // Prevent scrolling to bottom
    textarea.style.opacity = '0'; // Hide the textarea
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.popupService.notify(
          'Profile URL copied to clipboard!',
          'Profile URL',
          'success'
        );
      } else {
        console.error('Fallback: Copy command was unsuccessful');
      }
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
    }
    document.body.removeChild(textarea);
  }

  // ─── Livespace ────────────────────────────────────────────────────────

  openLivespaceConnect(): void {
    this.showLivespaceConnectDialog = true;
  }

  closeLivespaceConnect(): void {
    this.showLivespaceConnectDialog = false;
  }

  onLivespaceConnected(): void {
    this.showLivespaceConnectDialog = false;
    this.isConnectedToLivespace = true;

    const updatedUser = { ...this.user, has_connected_to_livespace: true };
    this.store.dispatch(updateUser({ user: updatedUser }));

    this.popupService.notify(
      'You have successfully connected to Livespace!',
      'Livespace connected',
      'success'
    );
  }

  disconnectLivespace(): void {
    this.store.dispatch(LivespaceActions.livespaceDisconnect());

    this.store.select(selectLivespaceConnected).pipe(
      filter((connected) => connected === false),
      take(1)
    ).subscribe(() => {
      this.isConnectedToLivespace = false;
      const updatedUser = { ...this.user, has_connected_to_livespace: false };
      this.store.dispatch(updateUser({ user: updatedUser }));

      this.popupService.notify(
        "You've successfully disconnected from Livespace",
        'Livespace Logout',
        'success'
      );
    });
  }

  /**
   * Reset and start the tour again
   */
  resetAndStartTour(): void {
    // Reset the tour state
    this.appTourService.resetTourState();

    // Navigate to dashboard first (if not already there)
    if (this.router.url !== '/' && this.router.url !== '/dashboard') {
      this.router.navigate(['/dashboard']).then(() => {
        // Start the tour after navigation completes
        setTimeout(() => {
          this.appTourService.startTour();
        }, 500);
      });
    } else {
      // Already on dashboard, just start the tour
      this.appTourService.startTour();
    }

    console.log('Tour reset and started again');
  }

}
